/**
 * Full SSR (treatment) entry for the model tag detail route.
 * Mounted via `dynamicWithExperiment` from
 * `routes/_app.tags.model.$tag.tsx` whenever the global
 * `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to `treatment`.
 *
 * Verbatim move of the previous inline `default` Component:
 * - Builds a route-scoped Apollo client seeded with `GetTack` and
 *   `ListGenerationModels` payloads from the loader so `useQuery`
 *   hits cache on first render.
 * - Falls back to `TagPageShell` when the loader skipped the
 *   tagShell (typically when the tag was not found).
 * - Passes `initialTagSlug` / `initialDisplayName` / `initialTack` /
 *   `initialModelConnectionData` down to `TagPage` so the first
 *   paint includes real models, not skeletons.
 *
 * The CSR (control) entry lives in `_app.tags.model.$tag.csr.tsx`
 * and ignores all of these branches; it relies on the global
 * `RootRuntime` Apollo client.
 */
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useLoaderData } from 'react-router'
import { TagPageShell } from 'src/components/Tag/TagPageShell'
import { TagPage } from 'src/pages/AppPage/TagPage'
import type { loader } from 'src/routes/_app.tags.model.$tag'
import {
  GetTackDocument,
  ListGenerationModelsDocument,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'

export default function ModelTagFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const fallback = loaderData.tagShell ? (
    <TagPageShell shell={loaderData.tagShell} />
  ) : null

  const client = useMemo(() => {
    return createRouteApolloClient([
      ...(loaderData.modelTagApollo.tackData
        ? [
            {
              query: GetTackDocument,
              variables: { codeName: loaderData.modelTagApollo.tagSlug },
              data: loaderData.modelTagApollo.tackData,
            },
          ]
        : []),
      ...(loaderData.modelTagApollo.listData
        ? [
            {
              query: ListGenerationModelsDocument,
              variables: loaderData.modelTagApollo.listVariables,
              data: loaderData.modelTagApollo.listData,
            },
          ]
        : []),
    ])
  }, [loaderData.modelTagApollo])

  if (!loaderData.tagShell || !client) {
    return fallback
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <div
          data-model-tag-preview-connection={
            loaderData.initialModelConnectionData ? '1' : '0'
          }
          data-model-tag-preview-edge-count={
            loaderData.previewConnectionEdgeCount
          }
          hidden
        />
        <TagPage
          type="model"
          initialTagSlug={loaderData.modelTagApollo.tagSlug}
          initialDisplayName={loaderData.tagShell.displayName}
          initialTack={loaderData.modelTagApollo.tackData?.tack}
          initialModelConnectionData={
            loaderData.initialModelConnectionData ??
            loaderData.modelTagApollo.listData?.generationModels
          }
        />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
